// Análises do dashboard (PLANO 4.4). Puro: recebe leads e agendamentos,
// devolve os recortes que a tela desenha. Reaproveita taxasDoFunil do módulo de
// metas para que dashboard e painel de metas nunca contem de jeitos diferentes.

import { dataFechamento, paraData, pct, taxasDoFunil } from '@/metas/calculo';
import { conversaoDeValor, dataPerda, semOrcamentoDeclarado, temDesfechoNegativo, valorOrcadoDe } from '@/leads/oportunidade';
import { comparecimentosNoPeriodo } from '@/leads/contagemComparecimento';
import { dataDoDesfechoNegativo } from '@/leads/dataDesfecho';
import { ETAPAS_PADRAO } from '@/lib/constants';
import { ehSemOrigem, origemPorId } from '@/leads/origens';

export const PERIODOS = [
  { id: '7', label: 'Últimos 7 dias', dias: 7 },
  { id: '30', label: 'Últimos 30 dias', dias: 30 },
  { id: '90', label: 'Últimos 90 dias', dias: 90 },
  { id: 'tudo', label: 'Todo o período', dias: null },
];

/** Início do recorte, ou null quando o período é "tudo". */
export function inicioDoPeriodo(periodoId, agora = new Date()) {
  const periodo = PERIODOS.find((p) => p.id === periodoId);
  if (!periodo?.dias) return null;
  const inicio = new Date(agora);
  inicio.setDate(inicio.getDate() - periodo.dias);
  inicio.setHours(0, 0, 0, 0);
  return inicio;
}

/**
 * ★ A-7a — A CÓPIA LOCAL SAIU. Era idêntica, byte a byte, à de `metas/calculo.js`; a grade
 * diária precisava da mesma regra e seria a TERCEIRA cópia. ⚠ Nenhum número muda: a
 * implementação é a mesma, só passou a ter um dono.
 */

/**
 * ★ A frase em cinza abaixo da rosca: o que chama atenção no quadro.
 *
 * Duas coisas, e as duas são perguntas que o gestor faria olhando a legenda:
 * quem traz VOLUME, e quem tem a melhor TAXA apesar de aparecer pouco. Se as
 * duas forem a mesma origem, a frase diz só a primeira — repetir não informa.
 *
 * `null` quando não há o que destacar: uma frase inventada é pior que nenhuma.
 */
export function destacarOrigem(porOrigem = []) {
  const comLead = porOrigem.filter((o) => o.leads > 0);
  if (comLead.length === 0) return null;
  const total = comLead.reduce((t, o) => t + o.leads, 0);
  const maior = comLead.reduce((a, b) => (b.leads > a.leads ? b : a));
  // A melhor taxa entre as OUTRAS, e só vale se ela de fato fechou alguém.
  const outras = comLead.filter((o) => o.id !== maior.id && o.fechamentos > 0);
  const melhor = outras.length
    ? outras.reduce((a, b) => (b.fechamentos / b.leads > a.fechamentos / a.leads ? b : a))
    : null;
  return {
    maior: { label: maior.label, leads: maior.leads, fechamentos: maior.fechamentos, parte: pct(maior.leads, total) },
    melhor: melhor && melhor.fechamentos / melhor.leads > maior.fechamentos / Math.max(1, maior.leads)
      ? { label: melhor.label, leads: melhor.leads, fechamentos: melhor.fechamentos }
      : null,
  };
}

/** ★ PC-7 — rótulo do lead sem anúncio no "Resultado por anúncio". */
export const SEM_ANUNCIO = 'Sem anúncio';
const limpo = (v) => (typeof v === 'string' ? v.trim() : '');
/**
 * Chave do anúncio do lead: o NOME que o gestor deu à frase (`anuncioNome`, PC-7), senão o TÍTULO
 * do cartão da Meta (`anuncioTitulo`), senão "Sem anúncio". ★ O nome vem antes porque o cartão da
 * Meta tem só 2 títulos para 7 anúncios (medição PC-7 §4); a frase separa o tema.
 */
export const chaveDoAnuncio = (lead) => limpo(lead?.anuncioNome) || limpo(lead?.anuncioTitulo) || SEM_ANUNCIO;

/** Média que devolve `null` — nunca 0 — quando não há denominador (D4/D5). */
const media = (total, quantos) => (quantos > 0 ? total / quantos : null);

/**
 * Consolida um período livre.
 *
 * ★★ WK (29/set/2026) — O QUE MUDOU, e por quê.
 *
 * 1. **Comparecimento é POR PESSOA** (D1), pela regra única de
 *    `@/leads/comparecimento` — a mesma que a comissão já usa. Um paciente que
 *    voltou duas vezes no mês conta UMA.
 * 2. **Fechamento é só EVENTO.** Antes a linha era
 *    `leadsPeriodo.filter(etapa fechado && dentro(dataFechamento))` — safra E
 *    evento ao mesmo tempo, e um lead de julho que fechasse em setembro não
 *    aparecia. Medido: **+16 fechamentos e +R$ 22.150 em 90 dias**.
 * 3. **`escopo`** (D6): `'safra'` conta só o que pertence a lead criado na
 *    janela; `'tudo'` conta todo evento da janela. **`leads` não muda nos dois** —
 *    lead é sempre da safra, por definição.
 * 4. **`fim`**, **`origem`**, e os **dois tickets**.
 *
 * @param escopo  'safra' | 'tudo'  (padrão 'tudo' — é o que o dono pediu)
 * @param origem  id de origem para filtrar, ou '' para todas
 */
export function analisar({
  leads = [],
  agendamentos = [],
  inicio = null,
  fim = null,
  escopo = 'tudo',
  origens = [],
  config = null,
}) {
  const dentro = (data) => data && (!inicio || data >= inicio) && (!fim || data <= fim);

  /**
   * ★★ O filtro de ORIGEM é MÚLTIPLO (29/set): o dono marca "Meta Ads" **e**
   * "Google Business" para ver só o marketing digital. **Nenhuma marcada = todas** —
   * lista vazia é ausência de filtro, não conjunto vazio.
   *
   * Vem antes de tudo porque estreita o universo, e os quatro números do funil
   * precisam falar da mesma gente.
   */
  const filtro = new Set(origens ?? []);
  const temFiltro = filtro.size > 0;
  const universo = temFiltro
    ? leads.filter((l) => filtro.has(ehSemOrigem(l.origem) ? '' : l.origem))
    : leads;
  const idsUniverso = new Set(universo.map((l) => l.id));
  const agsUniverso = temFiltro
    ? agendamentos.filter((a) => idsUniverso.has(a.leadId))
    : agendamentos;

  const leadsPeriodo = universo.filter((l) => dentro(paraData(l.criadoEm)));
  // ★ `escopo === 'safra'` amarra o evento ao lead da safra; 'tudo' solta.
  const idsSafra = new Set(leadsPeriodo.map((l) => l.id));
  const daSafra = (leadId) => escopo !== 'safra' || idsSafra.has(leadId);

  const marcados = agsUniverso.filter((a) => dentro(paraData(a.criadoEm)) && daSafra(a.leadId));
  // ★★ POR PESSOA (D1) — um por lead, pela regra única.
  const compareceram = comparecimentosNoPeriodo(universo, agsUniverso, dentro)
    .filter(({ lead }) => daSafra(lead.id));
  // ★★ Só EVENTO: o `leadsPeriodo` saiu daqui (ver o cabeçalho, item 2).
  const fechados = universo.filter(
    (l) => l.etapa === 'fechado' && dentro(dataFechamento(l)) && daSafra(l.id),
  );
  /**
   * ⚠ MUDANÇA ALÉM DA LISTA LITERAL DO PEDIDO, e por isso está escrita.
   *
   * Antes: `leadsPeriodo.filter(temDesfechoNegativo)` — safra, e **sem nenhum
   * recorte de data da perda**: um lead de setembro encerrado em dezembro
   * contava como perdido de setembro. Com os fechamentos virando evento, manter
   * o perdido como estava deixaria os dois lados do desfecho em critérios
   * diferentes — e a `conversaoDeValor` usa OS DOIS.
   * ★ Efeito medido no cenário de teste: perdidos 1 → 2.
   * ★★ APROVADO PELO DONO em 29/set/2026, depois de levantada: o recorte por
   * data da perda FICA. Era mudança fora do pedido literal, foi levada como tal
   * e confirmada — não é decisão minha que passou despercebida.
   */
  const perdidos = universo.filter(
    (l) => temDesfechoNegativo(l) && dentro(dataPerda(l)) && daSafra(l.id),
  );
  // VE: conversão de valor do período — só quem tem desfecho (o perdido pela
  // data da perda, não pela criação, para o recorte não depender de quando o
  // lead entrou no funil).
  const conversaoValor = conversaoDeValor([...fechados, ...perdidos]);

  const valor = fechados.reduce((t, l) => t + (Number(l.valorFechado) || 0), 0);

  /**
   * ★★ D4 — TICKET MÉDIO DE OPORTUNIDADE.
   *
   * Denominador = **quem compareceu**, tirando APENAS quem tem a marca
   * `semOrcamento === true`. Quem compareceu e ficou com o valor **em branco
   * CONTINUA** no denominador — decisão do dono, e é o que torna o número
   * honesto: branco é orçamento que ninguém registrou, não orçamento que não
   * existiu.
   *
   * ⚠ Medido em 29/set: **0 leads** têm a marca em toda a base (a WH subiu em
   * 29/set e ninguém usou a caixa). Hoje o denominador é, na prática, todo mundo
   * que compareceu — e 39 dos 62 de setembro entram com R$ 0,00.
   */
  const baseOportunidade = compareceram.filter(({ lead }) => !semOrcamentoDeclarado(lead));
  const totalOportunidade = baseOportunidade.reduce((t, { lead }) => t + valorOrcadoDe(lead), 0);

  const totais = {
    leads: leadsPeriodo.length,
    agendados: marcados.length,
    comparecimentos: compareceram.length,
    fechamentos: fechados.length,
    perdidos: perdidos.length,
    valor,
    totalOportunidade,
    // ★ `null`, nunca 0: "não dá para saber" não é "zero reais".
    ticketOportunidade: media(totalOportunidade, baseOportunidade.length),
    ticketVenda: media(valor, fechados.length),
  };

  /**
   * ★★ 29/set — O PAINEL PASSOU A CONTAR O EVENTO DA JANELA, não a etapa de HOJE.
   *
   * ⚠ O dono viu na tela: funil dizendo "Fecharam 24" e o painel dizendo
   * "Fechou 40". Os dois estavam certos e mediam coisas diferentes — o painel
   * mostrava onde o lead está AGORA, incluindo quem fechou meses atrás. A
   * decisão foi que os dois falem do MESMO período.
   *
   * ★ Cada lead entra numa cesta só, pelo evento MAIS AVANÇADO que ele teve
   * dentro da janela. A precedência é a ordem do funil, de baixo para cima:
   *
   *     fechou  >  encerrou sem venda  >  compareceu  >  agendou  >  entrou
   *
   * ★★ A CONSEQUÊNCIA QUE IMPORTA, e é o que o dono pediu: a cesta `fechado` é
   * **exatamente** `fechados` — o mesmo conjunto do último degrau do funil. Os
   * dois números não podem divergir porque são a mesma lista.
   *
   * ⚠ O QUE SE PERDE, dito de frente: um lead criado na janela cuja etapa de
   * hoje é `em_contato` aparece como "Novo", porque entrar foi o único evento
   * dele no período. O painel deixou de responder "onde ele está" e passou a
   * responder "o que aconteceu com ele aqui". Era a troca pedida.
   */
  /**
   * ★★ A DATA DE CADA ETAPA NEGATIVA — corrigido em 30/set, depois de medir.
   *
   * ⚠ O DEFEITO: a cesta usava só `perdidos`, que é `desfechoNegativoEm` — um
   * carimbo gravado **apenas quando o motivo ENCERRA** (`leadsService.js:330`).
   * Medido em produção: **299 de 5.515** em `nao_agendou` têm esse carimbo, e
   * **ZERO** dos 346 em `nao_fechou` e dos 556 em `nao_compareceu`. Resultado
   * na tela: "Não compareceu 0" em 30 dias, e 317 leads que não agendaram
   * aparecendo como "Novo".
   *
   * ★★ A HIPÓTESE ERA "não têm data". A medição derrubou: **as três têm**, em
   * três lugares diferentes — e o código lia justamente o único que quase
   * ninguém preenche.
   *
   *   `nao_agendou`     → `motivoNaoAgendouEm`   (5.515/5.515 preenchidos)
   *   `nao_fechou`      → `motivoNaoFechouEm`    (346/346)
   *   `nao_compareceu`  → o agendamento `faltou` (524 dos 556 leads)
   *
   * `desfechoNegativoEm` fica como RESERVA dos três — é o que cobre quem tem o
   * carimbo e não tem o campo próprio.
   *
   * ⚠ PENDÊNCIA CONHECIDA, e ela NÃO foi consertada aqui: **32 dos 556 leads em
   * `nao_compareceu` não têm agendamento `faltou` nem carimbo** (5,8 %). Eles
   * continuam sem data e, se tiverem entrado na janela, caem em "Novo".
   * Resolver exige gravar um carimbo próprio na etapa — escrita de dados, outra
   * peça. Há um teste que AFIRMA esse comportamento, para ninguém achar que
   * sumiu.
   */
  const faltaPorLead = new Map();
  for (const a of agsUniverso) {
    if (a?.status !== 'faltou') continue;
    const quando = paraData(a.dataHora);
    if (!quando) continue;
    const atual = faltaPorLead.get(a.leadId);
    // a falta MAIS RECENTE: é o último desencontro, o que explica o estado de hoje
    if (!atual || quando > atual) faltaPorLead.set(a.leadId, quando);
  }

  // ★★ A regra mora em `leads/dataDesfecho.js` — o MESMO arquivo que o script
  // retroativo importa. Não é espelho: é uma função só, vista de dois mundos.
  const dataDoDesfecho = (lead) =>
    dataDoDesfechoNegativo(lead, faltaPorLead.get(lead.id) ?? null).data;

  const cesta = new Map();
  const porId = (l) => l.id;
  for (const l of leadsPeriodo) cesta.set(porId(l), 'novo');
  for (const a of marcados) if (idsUniverso.has(a.leadId)) cesta.set(a.leadId, 'agendado');
  for (const { lead } of compareceram) cesta.set(porId(lead), 'compareceu');
  // Encerrado sem venda: vale a etapa NEGATIVA em que ele está, pela data DELA.
  for (const l of universo) {
    const quando = dataDoDesfecho(l);
    if (quando && dentro(quando)) cesta.set(porId(l), l.etapa);
  }
  // ★★ POR ÚLTIMO, e é o que garante "Fechou do painel = último degrau do
  // funil": quem fechou DENTRO da janela sobrescreve qualquer outra cesta.
  for (const l of fechados) cesta.set(porId(l), 'fechado');

  const baseEtapa = universo.filter((l) => cesta.has(l.id));
  const moveramNoPeriodo = baseEtapa.length;

  // ★ Quantos dos que se moveram não são da safra — a nota dos painéis.
  const deSafrasAnteriores = moveramNoPeriodo - leadsPeriodo.length;

  // ---- distribuição por etapa ----
  const porEtapa = ETAPAS_PADRAO.map((etapa) => {
    const doEtapa = baseEtapa.filter((l) => cesta.get(l.id) === etapa.id);
    return {
      ...etapa,
      quantidade: doEtapa.length,
      valor: doEtapa.reduce((t, l) => t + (Number(l.valorFechado) || 0), 0),
      participacao: pct(doEtapa.length, moveramNoPeriodo),
    };
  });

  // ---- desempenho por origem (PLANO 4.4: leads → agendados → fechados → valor) ----
  const agendamentoPorLead = new Map();
  for (const a of marcados) agendamentoPorLead.set(a.leadId, true);

  const porOrigemMapa = new Map();
  const linhaOrigem = (id) => {
    if (!porOrigemMapa.has(id)) {
      porOrigemMapa.set(id, {
        id,
        // VH: rótulo pela lista da clínica; legada com "(antigo)"; vazio é "Sem origem".
        label: id ? origemPorId(config, id).label : 'Sem origem',
        leads: 0,
        agendados: 0,
        fechamentos: 0,
        valor: 0,
      });
    }
    return porOrigemMapa.get(id);
  };

  // ★ A pizza acompanha o mesmo conjunto do painel de etapas (D6): os dois
  // respondem "de onde vêm estes leads", e precisam ser os MESMOS leads.
  for (const l of baseEtapa) {
    // VH-1: legado é lido como vazio — entra em "Sem origem".
    const linha = linhaOrigem(ehSemOrigem(l.origem) ? '' : l.origem);
    linha.leads += 1;
    if (agendamentoPorLead.has(l.id)) linha.agendados += 1;
    if (l.etapa === 'fechado') {
      linha.fechamentos += 1;
      linha.valor += Number(l.valorFechado) || 0;
    }
  }

  const porOrigem = [...porOrigemMapa.values()]
    .map((o) => ({
      ...o,
      taxaAgendamento: pct(o.agendados, o.leads),
      taxaFechamento: pct(o.fechamentos, o.leads),
      ticketMedio: o.fechamentos > 0 ? o.valor / o.fechamentos : 0,
    }))
    .sort((a, b) => b.leads - a.leads);

  /**
   * ★★★ PC-7 — RESULTADO POR ANÚNCIO (pedido do dono, 24/out: *"quantos leads chegaram, percentual
   * de agendamento, comparecimento, fechamento"*).
   *
   * ★ Agrupa os QUATRO conjuntos que o funil acabou de contar — `leadsPeriodo`, `marcados`,
   * `compareceram`, `fechados` — pela chave do anúncio do lead (`chaveDoAnuncio`). ★★ São os mesmos
   * conjuntos, então valem as MESMAS regras do funil: lead pelo mês de `criadoEm`, resgate fora de
   * denominador, comparecimento 1 por lead, fechamento por evento, escopo e filtro de origem (o
   * agrupamento parte do `universo`, já filtrado). ★ Invariante: Σ das linhas = os degraus do funil.
   *
   * ⚠⚠ NÃO chamar `analisar` uma vez por grupo: sem filtro de origem, `agsUniverso` é a lista
   * INTEIRA de agendamentos (acima), e cada linha contaria os agendamentos de todo mundo. Medido na
   * simulação do plano: 354 em cada linha, Σ 3.540 contra 354 no funil.
   *
   * ⚠ D5 do dono: este cartão conta como o FUNIL; o "por origem" conta pela CESTA (`baseEtapa`) e
   * fecha pela etapa de hoje. Os dois podem não somar igual — registrado, não unificado.
   */
  const anuncioPorLead = new Map(universo.map((l) => [l.id, chaveDoAnuncio(l)]));
  const chaveDoLeadId = (leadId) => anuncioPorLead.get(leadId) ?? SEM_ANUNCIO;
  const porAnuncioMapa = new Map();
  const linhaAnuncio = (chave) => {
    if (!porAnuncioMapa.has(chave)) {
      porAnuncioMapa.set(chave, { chave, leads: 0, agendados: 0, comparecimentos: 0, fechamentos: 0, valor: 0 });
    }
    return porAnuncioMapa.get(chave);
  };
  for (const l of leadsPeriodo) linhaAnuncio(chaveDoLeadId(l.id)).leads += 1;
  for (const a of marcados) linhaAnuncio(chaveDoLeadId(a.leadId)).agendados += 1;
  for (const { lead } of compareceram) linhaAnuncio(chaveDoLeadId(lead.id)).comparecimentos += 1;
  for (const l of fechados) {
    const linha = linhaAnuncio(chaveDoLeadId(l.id));
    linha.fechamentos += 1;
    linha.valor += Number(l.valorFechado) || 0;
  }
  const porAnuncio = [...porAnuncioMapa.values()]
    .map((x) => ({ ...x, taxas: taxasDoFunil(x) }))
    .sort((a, b) => (a.chave === SEM_ANUNCIO) - (b.chave === SEM_ANUNCIO) || b.leads - a.leads || a.chave.localeCompare(b.chave));

  /**
   * ★★ WK — "desempenho por pessoa" SAIU daqui (D7).
   *
   * Eram ~60 linhas que montavam `porPessoa` e `porSdr`. Medido antes de apagar
   * (M6): `porSdr` **não tinha nenhum consumidor** fora do próprio teste — era o
   * "alias de transição (até a V6)" que o comentário anunciava —, e `porPessoa`
   * só era lido pela seção da DashboardPage que esta peça remove.
   *
   * ★ Quem quiser desempenho por pessoa usa o **Placar da equipe**, que é por mês
   * e traz as metas. `consolidarMes` continua calculando tudo aquilo.
   */

  return {
    totais,
    taxas: taxasDoFunil(totais),
    // ★ quantos dos que se moveram vieram de meses anteriores.
    deSafrasAnteriores,
    /**
     * ★★ Quantos leads SE MOVERAM no período — o total dos dois painéis.
     *
     * ⚠ Substitui o antigo `leadsConsiderados` e o `fechadosForaDaJanela`: com
     * o painel contando EVENTO da janela (e não a etapa de hoje), a diferença
     * que aqueles dois campos existiam para explicar **deixou de existir**.
     */
    moveramNoPeriodo,
    /**
     * ★★ Quem FECHOU na janela sem ter COMPARECIDO nela.
     *
     * É a causa medida da taxa acima de 100 %: os degraus contam eventos dentro
     * da janela, e quem fecha numa janela curta pode ter comparecido antes
     * dela. A tela usa este número para EXPLICAR em vez de imprimir um
     * percentual impossível.
     */
    fechouSemComparecerNaJanela: fechados.filter(
      (l) => !compareceram.some(({ lead }) => lead.id === l.id),
    ).length,
    destaqueOrigem: destacarOrigem(porOrigem),
    conversaoValor,
    porEtapa,
    porOrigem,
    // ★ PC-7 — Σ das linhas = os degraus do funil (ver o bloco acima).
    porAnuncio,
    maiorAnuncio: Math.max(1, ...porAnuncio.map((x) => x.leads)),
    // Maior valor da série — a régua das barras, para que todas compartilhem
    // a mesma escala em vez de cada uma normalizar por si.
    maiorEtapa: Math.max(1, ...porEtapa.map((e) => e.quantidade)),
    maiorOrigem: Math.max(1, ...porOrigem.map((o) => o.leads)),
  };
}
